import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { CheckCircle2, Loader2, Lock, Eye, EyeOff, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ConfirmAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Token de confirmation manquant');
      setLoading(false);
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/validate-token?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setTokenValid(true);
        setUserInfo(data);
      } else {
        const data = await response.json();
        setError(data.detail || 'Token invalide ou expire');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setConfirming(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/auth/confirm-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      if (response.ok) {
        setConfirmed(true);
        toast.success('Compte confirme avec succes !');
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de la confirmation');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#0F5156] mx-auto mb-4" />
          <p className="text-slate-500">Verification du token...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#0F5156] flex items-center justify-center shadow-lg">
            <FileText className="w-7 h-7 text-white" />
          </div>
          <div>
            <span className="text-xl font-bold text-slate-900 block">Nexus Compta</span>
            <span className="text-slate-500 text-sm">Confirmation de compte</span>
          </div>
        </div>

        {confirmed ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Compte confirme !</h2>
              <p className="text-slate-500 mb-6">
                Votre compte a ete active avec succes. Vous pouvez maintenant vous connecter.
              </p>
              <Button className="w-full bg-[#0F5156] hover:bg-[#0d464a]" onClick={() => navigate('/login')}>
                Se connecter
              </Button>
            </CardContent>
          </Card>
        ) : tokenValid ? (
          <Card>
            <CardHeader>
              <CardTitle>Definir votre mot de passe</CardTitle>
              <CardDescription>
                {userInfo?.email && (
                  <span>Bienvenue <strong>{userInfo.prenom} {userInfo.nom}</strong>. Definissez votre mot de passe pour activer votre compte.</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <form onSubmit={handleConfirm} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimum 8 caracteres"
                      className="pl-10 pr-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password && password.length < 8 && <p className="text-xs text-red-500">Minimum 8 caracteres</p>}
                  {password && password.length >= 8 && (
                    <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Longueur suffisante</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirmez votre mot de passe"
                      className="pl-10"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  {confirmPassword && password !== confirmPassword && <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>}
                  {confirmPassword && password === confirmPassword && password.length >= 8 && (
                    <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Les mots de passe correspondent</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#0F5156] hover:bg-[#0d464a]"
                  disabled={confirming || password.length < 8 || password !== confirmPassword}
                >
                  {confirming ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirmation...</>
                  ) : (
                    'Activer mon compte'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Token invalide</h2>
              <p className="text-slate-500 mb-6">
                {error || 'Le lien de confirmation est invalide ou a expire. Contactez votre administrateur.'}
              </p>
              <Button variant="outline" onClick={() => navigate('/login')}>
                Retour a la connexion
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ConfirmAccount;
